import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, Route, Switch } from 'react-router-dom';
import { CompatRoute } from 'react-router-dom-v5-compat';

import ErrorBoundaryFallback from 'components/atoms/ErrorBoundaryFallback';
import HelmetWrapper from 'components/atoms/HelmetWrapper';
import BackOfficeDrawerPusher from 'components/features/backOffice/BackOfficeDrawerPusher';
import BackOfficeNavigation from 'components/features/backOffice/BackOfficeNavigation';
import CiboSubscriptionListPageContainer from 'containers/_pages/CiboSubscriptionListPageContainer';
import BackOfficeDeviceMap from 'routes/BackOfficeDeviceMap';
import BackOfficeDevices from 'routes/BackOfficeDevices';
import BackOfficeEnterprises from 'routes/BackOfficeEnterprises';
import BackOfficeInvitations from 'routes/BackOfficeInvitations';
import BackOfficeOperations from 'routes/BackOfficeOperations';
import BackOfficeUsers from 'routes/BackOfficeUsers';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import { useReadyFlag } from 'utils/useReadyFlags';

function BackOffice(): JSX.Element {
  const canViewEnterpriseUsers = useMyUserHasInternalPermission({
    resource: ProtectedResource.ENTERPRISE_USER,
    action: ResourceAction.READ
  });
  const canViewInternalUsers = useMyUserHasInternalPermission({
    resource: ProtectedResource.INTERNAL_USER,
    action: ResourceAction.READ
  });
  const canAccessBackOfficeUsers = canViewEnterpriseUsers || canViewInternalUsers;
  const canAccessBackOfficeInvitations = useMyUserHasInternalPermission({
    resource: ProtectedResource.INVITATION,
    action: ResourceAction.READ
  });

  // Separating from internal access policy because the operations section can have
  // many different actions within it (e.g. ip-cameras, rain gauge data manipulation, etc.)
  const showOperationsSection = useReadyFlag('showOperations');

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <BackOfficeDrawerPusher>
        <>
          <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
            <HelmetWrapper section="Back Office" />
            <BackOfficeNavigation />
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
            <Switch>
              <Redirect exact from="/" to="/back-office/enterprises" />
              <CompatRoute
                exact
                path="/back-office/enterprises/:enterpriseId/cibo-subscriptions"
                component={CiboSubscriptionListPageContainer}
              />
              <Route
                path="/back-office/enterprises"
                component={BackOfficeEnterprises}
              />
              <Route
                path="/back-office/devices"
                component={BackOfficeDevices}
              />
              <Route
                path="/back-office/device-map"
                component={BackOfficeDeviceMap}
              />
              {showOperationsSection && (
                <Route
                  path="/back-office/operations"
                  component={BackOfficeOperations}
                />
              )}
              {canAccessBackOfficeUsers && <Route path="/back-office/users" component={BackOfficeUsers} />}
              {canAccessBackOfficeInvitations && (<Route
                path="/back-office/invitations"
                component={BackOfficeInvitations}
              />)}
              <Redirect from="/*" to="/back-office/enterprises" />
            </Switch>
          </ErrorBoundary>
        </>
      </BackOfficeDrawerPusher>
    </ErrorBoundary>
  );
}

export default BackOffice;
